import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import './Profile.css';

function Profile() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState({
    displayName: '',
    email: currentUser?.email || '',
    bio: '',
    favoriteGenres: [],
    readingGoal: 24,
    privacy: 'friends'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [currentUser]);

  const fetchProfile = async () => {
    if (!currentUser) return;
    
    try {
      const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
      if (profileDoc.exists()) {
        const data = profileDoc.data();
        setProfile({
          displayName: data.displayName || currentUser.displayName || '',
          email: currentUser.email,
          bio: data.bio || '',
          favoriteGenres: data.favoriteGenres || [],
          readingGoal: data.readingGoal || 24,
          privacy: data.privacy || 'friends'
        });
      } else {
        // Create initial profile
        setProfile(prev => ({
          ...prev,
          displayName: currentUser.displayName || '',
          email: currentUser.email
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    
    setSaving(true);
    try {
      await updateDoc(doc(db, 'userProfiles', currentUser.uid), {
        displayName: profile.displayName,
        bio: profile.bio,
        favoriteGenres: profile.favoriteGenres,
        readingGoal: profile.readingGoal,
        privacy: profile.privacy,
        updatedAt: new Date()
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      // In a real app, you'd show an error message
    } finally {
      setSaving(false);
    }
  };

  const handleGenreToggle = (genre) => {
    setProfile(prev => ({
      ...prev,
      favoriteGenres: prev.favoriteGenres.includes(genre)
        ? prev.favoriteGenres.filter(g => g !== genre)
        : [...prev.favoriteGenres, genre]
    }));
  };

  const popularGenres = [
    'Fiction', 'Non-fiction', 'Mystery', 'Romance', 'Sci-Fi', 'Fantasy',
    'Biography', 'History', 'Self-help', 'Business', 'Poetry', 'Drama',
    'Horror', 'Young Adult', 'Children', 'Philosophy', 'Science', 'Art'
  ];

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-header">
          <div className="profile-avatar">
            <div className="avatar-placeholder">
              {profile.displayName ? profile.displayName.charAt(0).toUpperCase() : currentUser?.email?.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="profile-info">
            <h1>{profile.displayName || 'Anonymous Reader'}</h1>
            <p className="profile-email">{profile.email}</p>
            <div className="profile-stats">
              <div className="stat">
                <span className="stat-number">0</span>
                <span className="stat-label">Friends</span>
              </div>
              <div className="stat">
                <span className="stat-number">0</span>
                <span className="stat-label">Books</span>
              </div>
              <div className="stat">
                <span className="stat-number">0</span>
                <span className="stat-label">Reviews</span>
              </div>
            </div>
          </div>
          <div className="profile-actions">
            {!isEditing ? (
              <button 
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
              >
                ✏️ Edit Profile
              </button>
            ) : (
              <div className="edit-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsEditing(false);
                    fetchProfile(); // Reset changes
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-section">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Display Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profile.displayName}
                    onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Enter your display name"
                  />
                ) : (
                  <p>{profile.displayName || 'Not set'}</p>
                )}
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <p>{profile.email}</p>
              </div>
              
              <div className="form-group full-width">
                <label>Bio</label>
                {isEditing ? (
                  <textarea
                    value={profile.bio}
                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself and your reading interests..."
                    rows={3}
                  />
                ) : (
                  <p>{profile.bio || 'No bio added yet'}</p>
                )}
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h3>Reading Preferences</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Annual Reading Goal</label>
                {isEditing ? (
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={profile.readingGoal}
                    onChange={(e) => setProfile(prev => ({ ...prev, readingGoal: parseInt(e.target.value) || 24 }))}
                  />
                ) : (
                  <p>{profile.readingGoal} books</p>
                )}
              </div>
              
              <div className="form-group">
                <label>Privacy Setting</label>
                {isEditing ? (
                  <select
                    value={profile.privacy}
                    onChange={(e) => setProfile(prev => ({ ...prev, privacy: e.target.value }))}
                  >
                    <option value="public">Public</option>
                    <option value="friends">Friends Only</option>
                    <option value="private">Private</option>
                  </select>
                ) : (
                  <p className="privacy-badge">{profile.privacy}</p>
                )}
              </div>
            </div>
            
            <div className="form-group full-width">
              <label>Favorite Genres</label>
              {isEditing ? (
                <div className="genre-grid">
                  {popularGenres.map(genre => (
                    <button
                      key={genre}
                      type="button"
                      className={`genre-tag ${profile.favoriteGenres.includes(genre) ? 'selected' : ''}`}
                      onClick={() => handleGenreToggle(genre)}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="genre-list">
                  {profile.favoriteGenres.length > 0 ? (
                    profile.favoriteGenres.map(genre => (
                      <span key={genre} className="genre-tag selected">{genre}</span>
                    ))
                  ) : (
                    <p>No favorite genres selected</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="profile-section">
            <h3>Social Activity</h3>
            <div className="social-links">
              <a href="/friends" className="social-link">
                👥 Manage Friends
              </a>
              <a href="/activity" className="social-link">
                📱 Activity Feed
              </a>
              <a href="/book-clubs" className="social-link">
                📚 My Book Clubs
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;